import { useCallback, useEffect, useState } from 'react';
import { TApi } from 'utils/api';
import Cookies from 'universal-cookie';
import { SEARCH_CONFIGURATIONS_SIZE } from 'pages/common/consts/pagination-params';
import { ENDPOINTS } from 'pages/common/consts/endpoints';
import { TSearchConfigurationsResponse, TProjectConfiguration } from 'pages/common/types';
import { getSearchConfigurationsBody } from '../helpers/get-search-configurations-body';
import { SAVED_CONFIGURATION_LAMINATE_COOKIE } from '../consts/cookies';

type TParams = {
  apiMethods: TApi;
  authorizedCustomerId?: string;
  unauthorizedCustomerId?: string;
  searchConfigurationsSize?: number;
  isRequestAvailable?: boolean;
  onSearchConfigurationsError?: () => void;
};

export const useSearchSavedConfigurations = ({
  apiMethods,
  authorizedCustomerId,
  unauthorizedCustomerId,
  searchConfigurationsSize = SEARCH_CONFIGURATIONS_SIZE,
  isRequestAvailable = true,
  onSearchConfigurationsError,
}: TParams) => {
  const [configurationItems, setConfigurationItems] = useState<TProjectConfiguration[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const getConfigurations = useCallback(async () => {
    try {
      if ((!authorizedCustomerId && !unauthorizedCustomerId) || !isRequestAvailable) {
        // Для активации блоков catch и finally
        throw new Error();
      }

      setIsLoading(true);

      const cookies = new Cookies();
      const preparedBody = getSearchConfigurationsBody(authorizedCustomerId, unauthorizedCustomerId);
      const response: TSearchConfigurationsResponse = await apiMethods.post(
        `${ENDPOINTS.SEARCH_CONFIGURATIONS}?page=0&size=${searchConfigurationsSize}`,
        { ...preparedBody, projectStatus: 'SAVED' }
      );
      const configurations = response.content.flatMap(el => el.configurations);

      if (configurations.length === 0) {
        cookies.remove(SAVED_CONFIGURATION_LAMINATE_COOKIE, { path: '/' });
      }

      setConfigurationItems(configurations);
      setIsError(false);
    } catch {
      setIsError(true);
      setConfigurationItems([]);

      onSearchConfigurationsError?.();
    } finally {
      setIsLoading(false);
      setIsLoaded(true);
    }
  }, [
    apiMethods,
    authorizedCustomerId,
    unauthorizedCustomerId,
    searchConfigurationsSize,
    isRequestAvailable,
    onSearchConfigurationsError,
  ]);

  useEffect(() => {
    getConfigurations();
  }, [apiMethods, getConfigurations, authorizedCustomerId, unauthorizedCustomerId, isRequestAvailable]);

  return {
    configurationItems,
    isResponseEmpty: isLoaded && !configurationItems.length,
    isLoading,
    isError,
    isLoaded,
    getConfigurations,
  };
};
